import React from 'react'
import { Table } from 'react-bootstrap'
import ScreenshotPlaceholder from '../../../components/ScreenshotPlaceholder'
import { Tip, Warn, Code, ArticleLink, Ext, TemplateBtn } from '../_helpers'

export const SECTION_ARTICLES = [
  {
    id: 'data-weather',
    category: 'data',
    title: 'Fichiers météo',
    summary: 'Fichiers optionnels qui améliorent la précision des prévisions solaires et éoliennes.',
    body: (
      <>
        <p>
          Les fichiers météo sont optionnels mais améliorent la précision des prévisions. Il en existe deux types.
        </p>
        <h5 className="mt-3">Météo solaire</h5>
        <Table bordered size="sm" className="small">
          <thead style={{ background: 'var(--color-neutral-100)' }}>
            <tr><th>Colonne</th><th>Requis ?</th><th>Signification</th></tr>
          </thead>
          <tbody>
            <tr><td><Code>timestamp</Code></td><td>Requis</td><td>L'heure</td></tr>
            <tr><td><Code>ghi_w_m2</Code></td><td>Requis</td><td>Intensité du rayonnement solaire, en watts par mètre carré</td></tr>
            <tr><td><Code>temperature_c</Code></td><td>Optionnel</td><td>Température de l'air, °C</td></tr>
            <tr><td><Code>cloud_cover_pct</Code></td><td>Optionnel</td><td>Couverture nuageuse, 0–100 %</td></tr>
            <tr><td><Code>dni_w_m2</Code></td><td>Optionnel</td><td>Rayonnement direct, W/m²</td></tr>
          </tbody>
        </Table>
        <TemplateBtn type="weather_solar">Modèle de météo solaire</TemplateBtn>

        <h5 className="mt-4">Météo éolienne</h5>
        <Table bordered size="sm" className="small">
          <thead style={{ background: 'var(--color-neutral-100)' }}>
            <tr><th>Colonne</th><th>Requis ?</th><th>Signification</th></tr>
          </thead>
          <tbody>
            <tr><td><Code>timestamp</Code></td><td>Requis</td><td>L'heure</td></tr>
            <tr><td><Code>wind_speed_m_s</Code></td><td>Requis</td><td>Vitesse du vent, en mètres par seconde</td></tr>
            <tr><td><Code>wind_direction_deg</Code></td><td>Optionnel</td><td>Direction du vent, 0–360°</td></tr>
            <tr><td><Code>temperature_c</Code></td><td>Optionnel</td><td>Température de l'air, °C</td></tr>
          </tbody>
        </Table>
        <TemplateBtn type="weather_wind">Modèle de météo éolienne</TemplateBtn>
        <Tip>Les colonnes optionnelles peuvent être omises entièrement — mais si vous incluez une colonne, renseignez-la pour chaque ligne.</Tip>

        <h5 className="mt-4">Où obtenir des données météo ?</h5>
        <p>
          Vous n'avez presque jamais besoin de les relever vous-même. Plusieurs services gratuits publient
          des données météo historiques horaires pour n'importe quel endroit en Europe. Commencez par les
          services universels — ils couvrent tous les pays pilotes et ne nécessitent pas de compte spécial.
        </p>

        <h6 className="mt-3">Open-Meteo (recommandé, le plus simple)</h6>
        <p>
          <Ext href="https://open-meteo.com/en/docs/historical-weather-api">open-meteo.com/en/docs/historical-weather-api</Ext>
          {' '}— gratuit, sans compte, historique horaire remontant à 1940.
        </p>
        <ol>
          <li>Ouvrez la page ci-dessus. Dans le formulaire, saisissez la <strong>latitude et la
            longitude</strong> de votre communauté (trouvez-les sur Google Maps — clic droit sur le lieu → « What's here »).</li>
          <li>Définissez la <strong>date de début</strong> et la <strong>date de fin</strong> pour l'année souhaitée.</li>
          <li>Sous <strong>Hourly Variables</strong>, cochez les variables ci-dessous (ignorez la liste éolienne si
            vous n'avez besoin que du solaire, et vice-versa) :
            <ul>
              <li><Code>shortwave_radiation</Code> — correspond au GHI (en W/m²).</li>
              <li><Code>direct_radiation</Code> — correspond au DNI (optionnel).</li>
              <li><Code>temperature_2m</Code> — température de l'air (°C).</li>
              <li><Code>cloud_cover</Code> — couverture nuageuse (%).</li>
              <li><Code>wind_speed_10m</Code> — vitesse du vent à 10 m (m/s).</li>
              <li><Code>wind_direction_10m</Code> — direction du vent (degrés).</li>
            </ul>
          </li>
          <li>Définissez le format sur <strong>CSV</strong> et cliquez sur <strong>Download</strong>.</li>
          <li>Ouvrez le fichier. Renommez les colonnes selon ce qu'attend la plateforme :
            {' '}<Code>shortwave_radiation</Code> → <Code>ghi_w_m2</Code>,
            {' '}<Code>direct_radiation</Code> → <Code>dni_w_m2</Code>,
            {' '}<Code>temperature_2m</Code> → <Code>temperature_c</Code>,
            {' '}<Code>cloud_cover</Code> → <Code>cloud_cover_pct</Code>,
            {' '}<Code>wind_speed_10m</Code> → <Code>wind_speed_m_s</Code>,
            {' '}<Code>wind_direction_10m</Code> → <Code>wind_direction_deg</Code>.</li>
          <li>Assurez-vous que la colonne d'horodatage est nommée <Code>timestamp</Code>. Enregistrez en CSV UTF-8 et
            téléversez.</li>
        </ol>

        <h6 className="mt-3">PVGIS — Commission européenne JRC (idéal pour la planification solaire)</h6>
        <p>
          <Ext href="https://re.jrc.ec.europa.eu/pvg_tools/en/">re.jrc.ec.europa.eu/pvg_tools/en/</Ext>
          {' '}— gratuit, sans compte, couvre toute l'Europe. Particulièrement utile lorsque vous avez besoin
          d'une année « typique » de référence (TMY) plutôt qu'une année historique spécifique.
        </p>
        <ol>
          <li>Ouvrez le site et choisissez votre emplacement sur la carte.</li>
          <li>Sélectionnez <strong>« Hourly data »</strong> dans les onglets de gauche.</li>
          <li>Cochez <strong>Solar radiation components</strong> et <strong>2-m air temperature</strong>,
            sélectionnez une année (ou une plage), puis cliquez sur <strong>Download CSV</strong>.</li>
          <li>Le fichier téléchargé contient un petit en-tête qu'il peut être nécessaire de supprimer pour que la ligne
            de colonnes (<Code>time</Code>, <Code>G(i)</Code>, <Code>T2m</Code>, …) se trouve à la ligne 1. Renommez
            {' '}<Code>G(i)</Code> → <Code>ghi_w_m2</Code> et <Code>T2m</Code> → <Code>temperature_c</Code>.</li>
        </ol>
        <Tip>Les horodatages PVGIS sont généralement en UTC et peuvent ressembler à <Code>20250101:1000</Code>
          — convertissez-les en <Code>2025-01-01T10:00:00Z</Code> à l'aide de la formule de
          {' '}<ArticleLink to="data-csv-basics">Créer des fichiers CSV correctement</ArticleLink>.</Tip>

        <h6 className="mt-3">Renewables.ninja (production solaire / éolienne par emplacement)</h6>
        <p>
          <Ext href="https://www.renewables.ninja">renewables.ninja</Ext> — gratuit avec une inscription rapide.
          Entrez des coordonnées, choisissez « MERRA-2 » ou « ERA-5 », l'année et la résolution, et
          téléchargez le CSV. La variable à mapper est <Code>irradiance</Code> → <Code>ghi_w_m2</Code> pour
          le solaire, et <Code>wind_speed</Code> → <Code>wind_speed_m_s</Code> pour l'éolien.
        </p>

        <h6 className="mt-3">NASA POWER (solution de repli mondiale)</h6>
        <p>
          <Ext href="https://power.larc.nasa.gov/data-access-viewer/">power.larc.nasa.gov/data-access-viewer</Ext>
          {' '}— données horaires mondiales, gratuites. Utile quand d'autres sources ne couvrent pas l'emplacement.
          Noms des variables : <Code>ALLSKY_SFC_SW_DWN</Code> → <Code>ghi_w_m2</Code>,
          {' '}<Code>T2M</Code> → <Code>temperature_c</Code>,
          {' '}<Code>WS10M</Code> → <Code>wind_speed_m_s</Code>.
        </p>

        <h6 className="mt-3">Services météorologiques nationaux</h6>
        <Table bordered size="sm" responsive className="small">
          <thead style={{ background: 'var(--color-neutral-100)' }}>
            <tr><th>Pays</th><th>Service</th></tr>
          </thead>
          <tbody>
            <tr><td>Italie</td><td>ISPRA / ARPA régional (ex. ARPA Lombardia, ARPA Veneto) — données horaires par station et par région.</td></tr>
            <tr><td>France</td><td>Portail open-data Météo-France (<Ext href="https://meteo.data.gouv.fr">meteo.data.gouv.fr</Ext>) — observations horaires historiques.</td></tr>
            <tr><td>Allemagne</td><td>DWD Climate Data Center (<Ext href="https://opendata.dwd.de">opendata.dwd.de</Ext>) — gratuit, données horaires par station.</td></tr>
            <tr><td>Rép. tchèque</td><td>CHMI (<Ext href="https://www.chmi.cz">chmi.cz</Ext>) — données historiques par station sur demande / via portail.</td></tr>
            <tr><td>Grèce</td><td>HNMS / NOA-Meteo (<Ext href="https://www.meteo.gr">meteo.gr</Ext>) — données historiques gratuites limitées ; Open-Meteo est généralement plus simple.</td></tr>
          </tbody>
        </Table>

        <Warn>
          Faites correspondre l'<strong>année</strong> et le <strong>fuseau horaire</strong> de votre fichier météo
          à ceux de vos autres fichiers. Si vos données de charge concernent 2024 en heure locale, téléchargez
          les données météo 2024 en UTC et convertissez-les (Open-Meteo vous permet de choisir le fuseau horaire
          avant le téléchargement). Des années ou des fuseaux horaires mal assortis dégradent les prévisions au
          lieu de les améliorer.
        </Warn>
      </>
    ),
  },

  {
    id: 'data-bill',
    category: 'data',
    title: 'Fichiers de factures d\'énergie',
    summary: 'Données de facturation mensuelle utilisées pour l\'analyse des coûts.',
    body: (
      <>
        <p>
          Un fichier de <strong>Facture d'énergie</strong> liste les factures passées, avec une ligne par
          période de facturation (en général un mois). Il est utilisé pour l'analyse des coûts.
        </p>
        <Table bordered size="sm" className="small">
          <thead style={{ background: 'var(--color-neutral-100)' }}>
            <tr><th>Colonne</th><th>Requis ?</th><th>Signification</th></tr>
          </thead>
          <tbody>
            <tr><td><Code>period_start</Code></td><td>Requis</td><td>Premier jour de la facture, ex. <Code>2025-01-01</Code></td></tr>
            <tr><td><Code>period_end</Code></td><td>Requis</td><td>Dernier jour de la facture, ex. <Code>2025-01-31</Code></td></tr>
            <tr><td><Code>consumption_kwh</Code></td><td>Requis</td><td>Électricité totale consommée sur la période, en kWh</td></tr>
            <tr><td><Code>cost_eur</Code></td><td>Requis</td><td>Montant total payé, en euros</td></tr>
            <tr><td><Code>peak_demand_kw</Code></td><td>Optionnel</td><td>Puissance maximale soutirée sur la période, en kW</td></tr>
            <tr><td><Code>rate_eur_per_kwh</Code></td><td>Optionnel</td><td>Prix moyen par kWh</td></tr>
          </tbody>
        </Table>
        <pre style={{ background: 'var(--color-neutral-50)', padding: 12, borderRadius: 6, fontSize: '0.8rem' }}>
{`period_start,period_end,consumption_kwh,cost_eur
2025-01-01,2025-01-31,3850,962.50
2025-02-01,2025-02-28,3520,880.00`}
        </pre>
        <TemplateBtn type="building_energy_bill">Télécharger le modèle de facture d'énergie</TemplateBtn>
        <Warn>La date de début de chaque période doit être antérieure à sa date de fin, et les périodes ne doivent pas se chevaucher.</Warn>
      </>
    ),
  },
]
